import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useEnterExitAnimation } from '../../util/useEnterExitAnimation.js';
import { useEventListener } from '../../util/useEventListener.js';
import { useInteraction } from '../../util/useInteraction.js';
import { useOverlay } from '../../util/useOverlay.js';
import useUniqueId from '../../util/useUniqueId.js';
import { useEnvironmentContext } from '../../contexts/system/Environment.js';
import { ZINDEX } from '../../static/constants.js';
import { getBottomSheetHeight, getStaticProps, getFocusTrapProps, getHeaderProps } from './BottomSheet.mappers.js';
import { useBottomSheetHeader, getLabelledAndDescribedBy } from './BottomSheetHeader/useBottomSheetHeader.js';
import { BOTTOM_SHEET_TRANSITION, ANIMATION_FALL_BACK_TIME_MS, BOTTOM_SHEET_CLOSE_KEY } from './constants.js';

var _excluded = ["visible", "title", "subtitle", "height", "maxHeight", "header", "subtitleId", "dismissible", "onChange", "divider", "closeButton", "contentHeading", "focusableContainer", "zIndex", "usePortal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var useBottomSheet = function useBottomSheet(_ref) {
  var visible = _ref.visible,
    title = _ref.title,
    subtitle = _ref.subtitle,
    _ref$height = _ref.height,
    height = _ref$height === void 0 ? 'content-fit' : _ref$height,
    maxHeight = _ref.maxHeight,
    header = _ref.header,
    subtitleId = _ref.subtitleId,
    _ref$dismissible = _ref.dismissible,
    dismissible = _ref$dismissible === void 0 ? true : _ref$dismissible,
    onChange = _ref.onChange,
    _ref$divider = _ref.divider,
    divider = _ref$divider === void 0 ? true : _ref$divider,
    closeButton = _ref.closeButton,
    contentHeading = _ref.contentHeading,
    focusableContainer = _ref.focusableContainer,
    zIndex = _ref.zIndex,
    usePortal = _ref.usePortal,
    rest = _objectWithoutProperties(_ref, _excluded);
  var id = useUniqueId();
  var _React$useState = React.useState(window.innerHeight),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    screenHeight = _React$useState2[0],
    setScreenHeight = _React$useState2[1];
  var resizeHandler = React.useCallback(function () {
    return setScreenHeight(window.innerHeight);
  }, []);
  useEventListener({
    handler: resizeHandler,
    eventName: 'resize'
  });
  var _useBottomSheetHeader = useBottomSheetHeader({
      subtitle: subtitle,
      title: title,
      id: id
    }),
    bottomSheetHeaderProps = _useBottomSheetHeader.bottomSheetHeaderProps;
  var close = React.useCallback(function () {
    if (dismissible) onChange === null || onChange === void 0 || onChange(false);
  }, [dismissible, onChange]);
  var _useOverlay = useOverlay({
      visible: visible,
      transition: BOTTOM_SHEET_TRANSITION,
      animationFallBackTimeMs: ANIMATION_FALL_BACK_TIME_MS,
      onClick: close,
      zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : ZINDEX.FLOATING
    }),
    overlayProps = _useOverlay.overlayProps,
    shouldRenderOverlay = _useOverlay.shouldRenderOverlay;
  var _useEnterExitAnimatio = useEnterExitAnimation({
      initialProps: {
        transform: 'translateY(100%)'
      },
      enterProps: {
        transform: 'translateY(0%)'
      },
      animationOn: visible,
      transition: BOTTOM_SHEET_TRANSITION,
      animationFallBackTimeMs: ANIMATION_FALL_BACK_TIME_MS
    }),
    transitionPropsBottomSheet = _useEnterExitAnimatio.transitionProps,
    shouldRenderBottomSheet = _useEnterExitAnimatio.shouldRender;
  var onClick = React.useCallback(function (event) {
    return event.stopPropagation();
  }, []);
  var onKeyDown = React.useCallback(function (event) {
    return event.key === BOTTOM_SHEET_CLOSE_KEY && close();
  }, [close]);
  var closeOnClick = React.useCallback(function (_) {
    return close();
  }, [close]);
  var _useInteraction = useInteraction({
      onClick: onClick,
      onKeyDown: onKeyDown
    }),
    interactionProps = _useInteraction.interactionProps;
  var _useInteraction2 = useInteraction({
      onClick: closeOnClick
    }),
    closeButtonInteractionProps = _useInteraction2.interactionProps;
  var _getLabelledAndDescri = getLabelledAndDescribedBy(id, subtitleId),
    labelledBy = _getLabelledAndDescri.labelledBy,
    describedBy = _getLabelledAndDescri.describedBy;
  var heightProps = getBottomSheetHeight(screenHeight, height, maxHeight);
  var staticProps = getStaticProps();
  var focusTrapProps = getFocusTrapProps({
    visible: !!visible,
    transitionProps: transitionPropsBottomSheet
  });
  var headerProps = getHeaderProps({
    labelledBy: labelledBy,
    title: title,
    subtitleId: subtitleId,
    describedBy: describedBy,
    header: header
  });
  var _useEnvironmentContex = useEnvironmentContext(),
    getRootContainer = _useEnvironmentContex.getRootContainer,
    getScrollableContainer = _useEnvironmentContex.getScrollableContainer;
  var rootContainer = usePortal ? getRootContainer() : undefined;
  var createRef = function createRef(element) {
    var scrollableContainer = getScrollableContainer();
    var style = scrollableContainer.style;
    // TODO: ADSDEV-622 Prevent scroll with CSS architecture
    // body:has(role=dialog) { overflow: hidden }
    if (element) {
      style.overflow = 'hidden';
      style.contain = 'unset';
    } else {
      style.removeProperty('overflow');
      style.removeProperty('contain');
    }
  };
  var closeButtonProps = {
    variant: 'icon',
    hierarchy: 'tertiary',
    size: 's',
    mt: '24',
    me: '24'
  };
  var contentWrapperBoxProps = {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    safeArea: {
      bottom: true
    },
    touchAction: 'none'
  };
  var contentBoxProps = {
    overflowY: 'auto',
    tabIndex: focusableContainer !== false ? 0 : undefined
  };
  return _objectSpread(_objectSpread({
    containerBoxProps: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, staticProps), headerProps), heightProps), interactionProps), {}, {
      ref: createRef
    }),
    contentWrapperBoxProps: contentWrapperBoxProps,
    focusTrapProps: focusTrapProps,
    bottomSheetHeaderProps: bottomSheetHeaderProps,
    contentBoxProps: _objectSpread(_objectSpread({}, contentBoxProps), rest),
    divider: divider,
    contentHeading: contentHeading,
    headerElement: header,
    overlayProps: overlayProps,
    shouldRenderOverlay: shouldRenderOverlay,
    shouldRenderBottomSheet: shouldRenderBottomSheet
  }, dismissible && {
    closeButton: _objectSpread(_objectSpread(_objectSpread({}, closeButton), closeButtonInteractionProps), closeButtonProps)
  }), {}, {
    rootContainer: rootContainer
  });
};

export { useBottomSheet };
//# sourceMappingURL=useBottomSheet.js.map
